import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@tanium/react-modal';
import { Button, TooltippedButton } from '@tanium/react-button';
import { Stack } from '@tanium/react-stack';
import { DefinitionList, DefinitionListItem } from '@tanium/react-definition-list';
import { useAuthenticatedApiContext } from '../../api/useApiClient';
import { useAuthContext } from '../../auth';
import { AsyncStatus, useAsync } from '../../hooks/useAsync';
import { useTenant } from '../../hooks/useTenant';
import { useHasPermissions } from '../../hooks/useUserPermissions';
import { Permission } from '../../models/Permissions';
import { TenantDeletionEligibility } from '../../models/TenantModels';

// The root admin is the only local user and the only holder of this role; IdP role mappings
// cannot grant it.
export const ROOT_ADMIN_ROLE = 'super_admin';

interface UserInfoModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  onDeleteAccount: () => void;
}

export const UserInfoModal: React.VFC<UserInfoModalProps> = ({ isOpen, onRequestClose, onDeleteAccount }) => {
  const { t } = useTranslation();
  const apiClient = useAuthenticatedApiContext();
  const { loginUsername, loginEmail, loginRoles } = useAuthContext();
  const { tenant } = useTenant();
  const canDeleteAccount = useHasPermissions([Permission.DeleteTenantSelf]);

  const loadDeletionEligibility = useAsync(
    useCallback(async (): Promise<TenantDeletionEligibility> => apiClient.getTenantDeletionEligibility(), [apiClient])
  );
  const { execute: executeLoadDeletionEligibility } = loadDeletionEligibility;
  const deletionEligibility = loadDeletionEligibility.value;

  // The eligibility endpoint is permission-gated, so only callers who can delete ask for it. It is
  // re-read on every open so a change since the last look (e.g. a newly linked instance) shows.
  useEffect(() => {
    if (isOpen && canDeleteAccount) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      executeLoadDeletionEligibility();
    }
  }, [isOpen, canDeleteAccount, executeLoadDeletionEligibility]);

  const isRootAdmin = loginRoles.includes(ROOT_ADMIN_ROLE);
  // A local user's token carries the username in its email claim; the root admin's real address is
  // the tenant's admin email.
  const email = isRootAdmin ? tenant?.email : loginEmail;

  const getRoleLabel = (role: string): string => {
    switch (role) {
      case ROOT_ADMIN_ROLE:
        return t('Root Admin');
      case 'admin':
        return t('Admin');
      case 'audit_user':
        return t('Artifacts User');
      case 'user':
        return t('User');
      default:
        return role;
    }
  };
  const roleLabels = loginRoles.map(getRoleLabel).sort((a, b) => a.localeCompare(b));

  const getDeleteAccountTooltip = (): string | false => {
    if (loadDeletionEligibility.status === AsyncStatus.Failure) {
      return t("Couldn't check whether this account can be deleted");
    }
    switch (deletionEligibility?.blockedReason) {
      case 'msp':
        return t("Managed service provider accounts can't be deleted here. Contact Tanium support");
      case 'linked_instances':
        return t('Unlink all Tanium instances before deleting this account');
      default:
        return false;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      header={t('User Info')}
      size="lg"
      onRequestClose={onRequestClose}
      footer={
        <Stack inline itemSpacing={1}>
          <Button onClick={onRequestClose}>{t('Close')}</Button>
        </Stack>
      }
    >
      <Stack direction="column" itemSpacing={2}>
        <DefinitionList>
          {isRootAdmin && loginUsername && (
            <DefinitionListItem term={`${t('Username')}:`} description={loginUsername} />
          )}
          {email && <DefinitionListItem term={`${t('Email')}:`} description={email} />}
          {roleLabels.length > 0 && (
            <DefinitionListItem
              term={`${t('Roles')}:`}
              description={
                <ul>
                  {roleLabels.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ul>
              }
            />
          )}
        </DefinitionList>
        {canDeleteAccount && (
          <Stack alignItems="center" justifyContent="space-between" width="100%">
            <DefinitionList>
              <DefinitionListItem
                term={`${t('Delete Account')}:`}
                description={t('Permanently delete this Tanium Account')}
              />
            </DefinitionList>
            <TooltippedButton
              variant="secondary"
              disabled={!deletionEligibility?.eligible || loadDeletionEligibility.status === AsyncStatus.Failure}
              tooltipContent={getDeleteAccountTooltip()}
              onClick={onDeleteAccount}
            >
              {t('Delete Account')}
            </TooltippedButton>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};
